import { useEffect, useState, type ReactNode } from 'react';
import { speaker } from '../../engine/audio';
import { listCameras, type CameraChoice } from '../../engine/camera';
import { db, pruneClips, requestPersistence, storageEstimate, wipeEverything, type Settings as S } from '../../engine/db';
import { navigate, releaseSource, useApp } from '../app-state';
import { fmtDate, PageHead, Segmented, Toggle } from '../components/bits';
import { Icon } from '../components/Icon';

function Row({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <div className="setting">
      <div className="label">
        {label}
        {hint && <small>{hint}</small>}
      </div>
      {children}
    </div>
  );
}

export function Settings() {
  const app = useApp();
  const s = app.settings;
  const set = (p: Partial<S>) => app.updateSettings(p);
  const [voices, setVoices] = useState<SpeechSynthesisVoice[]>([]);
  const [cameras, setCameras] = useState<CameraChoice[]>([]);
  const [storage, setStorage] = useState<{ usage: number; quota: number; persisted: boolean } | null>(null);

  useEffect(() => {
    const load = () => setVoices(speaker.voices());
    load();
    if ('speechSynthesis' in window) speechSynthesis.addEventListener('voiceschanged', load);
    void listCameras().then(setCameras).catch(() => {});
    void storageEstimate().then(setStorage);
    return () => {
      if ('speechSynthesis' in window) speechSynthesis.removeEventListener('voiceschanged', load);
    };
  }, []);

  const test = () => {
    speaker.unlock();
    speaker.tone('ready');
    setTimeout(() => speaker.tone('go'), 600);
    setTimeout(() => void speaker.say('Front foot behind target. Back foot on target.'), 1100);
  };

  const mb = (b: number) => `${(b / 1e6).toFixed(b > 1e8 ? 0 : 1)} MB`;

  return (
    <div className="page stack">
      <PageHead title="Settings" />

      <div className="section-title">Practice</div>
      <div className="card flush list">
        <Row label="Attempts per set">
          <Segmented label="Attempts per set" value={s.attemptsPerSet} onChange={(v) => set({ attemptsPerSet: v })} options={[{ value: 5, label: '5' }, { value: 10, label: '10' }, { value: 20, label: '20' }, { value: 0, label: '∞' }]} />
        </Row>
        <Row label="Start cue" hint={{ auto: 'Beep once you’re lying still', timer: 'Beep every few seconds', none: 'No beep — pop up when you like' }[s.cueMode]}>
          <Segmented label="Start cue" value={s.cueMode} onChange={(v) => set({ cueMode: v })} options={[{ value: 'auto', label: 'Auto' }, { value: 'timer', label: 'Timer' }, { value: 'none', label: 'Self' }]} />
        </Row>
        <Row label="Random delay before the beep" hint="Stops you anticipating it, like a real wave">
          <Toggle label="Random delay" checked={s.randomDelay} onChange={(v) => set({ randomDelay: v })} />
        </Row>
        <Row label="Hold the landing for">
          <Segmented label="Hold" value={s.holdMs} onChange={(v) => set({ holdMs: v })} options={[{ value: 800, label: '0.8 s' }, { value: 1000, label: '1 s' }, { value: 1500, label: '1.5 s' }]} />
        </Row>
        <Row label="Target size" hint={{ relaxed: '±12 cm around each target', standard: '±8 cm around each target', strict: '±5 cm around each target' }[s.strictness]}>
          <Segmented label="Target size" value={s.strictness} onChange={(v) => set({ strictness: v })} options={[{ value: 'relaxed', label: 'Relaxed' }, { value: 'standard', label: 'Standard' }, { value: 'strict', label: 'Strict' }]} />
        </Row>
        <Row label="Check foot angle" hint="Experimental: also require each foot to point like your target">
          <Toggle label="Check foot angle" checked={s.checkAngle} onChange={(v) => set({ checkAngle: v })} />
        </Row>
        <Row label="Announce pop-up time" hint="Only on clean landings">
          <Toggle label="Announce time" checked={s.announceTime} onChange={(v) => set({ announceTime: v })} />
        </Row>
      </div>

      <div className="section-title">Sound</div>
      <div className="card flush list">
        <Row label="Spoken feedback">
          <Toggle label="Spoken feedback" checked={s.voice} onChange={(v) => set({ voice: v })} />
        </Row>
        <Row label="Beeps">
          <Toggle label="Beeps" checked={s.beeps} onChange={(v) => set({ beeps: v })} />
        </Row>
        {voices.length > 0 && (
          <Row label="Voice">
            <select className="input" style={{ maxWidth: 200 }} value={s.voiceURI ?? ''} onChange={(e) => set({ voiceURI: e.target.value || null })} aria-label="Voice">
              <option value="">Automatic</option>
              {voices.map((v) => (
                <option key={v.voiceURI} value={v.voiceURI}>
                  {v.name} ({v.lang})
                </option>
              ))}
            </select>
          </Row>
        )}
        <Row label="Speaking rate">
          <Segmented label="Speaking rate" value={s.speechRate} onChange={(v) => set({ speechRate: v })} options={[{ value: 0.9, label: 'Calm' }, { value: 1.05, label: 'Normal' }, { value: 1.25, label: 'Quick' }]} />
        </Row>
        <button className="list-item" onClick={test}>
          <Icon name="volume" />
          <span className="grow">Test sound</span>
        </button>
      </div>
      <p className="tiny faint" style={{ padding: '0 4px' }}>On iPhone, turn the volume up. Silent mode may mute beeps on older iOS versions.</p>

      <div className="section-title">Camera & pose model</div>
      <div className="card flush list">
        <Row label="Camera" hint="The physical 1× back camera keeps calibration stable">
          <select
            className="input"
            style={{ maxWidth: 200 }}
            value={s.cameraId ?? ''}
            onChange={(e) => {
              releaseSource();
              set({ cameraId: e.target.value || null });
            }}
            aria-label="Camera"
          >
            <option value="">Automatic (back camera)</option>
            {cameras.map((c) => (
              <option key={c.deviceId} value={c.deviceId}>
                {c.label}
              </option>
            ))}
          </select>
        </Row>
        <Row label="Resolution">
          <Segmented label="Resolution" value={s.resolution} onChange={(v) => (releaseSource(), set({ resolution: v }))} options={[{ value: 720, label: '720p' }, { value: 1080, label: '1080p' }]} />
        </Row>
        <Row label="Frame rate">
          <Segmented label="Frame rate" value={s.fps} onChange={(v) => (releaseSource(), set({ fps: v }))} options={[{ value: 30, label: '30' }, { value: 60, label: '60' }]} />
        </Row>
        <Row label="Pose model" hint={{ lite: 'Fastest, least precise', full: 'Balanced (recommended)', heavy: 'Most precise, slowest; ~30 MB download on first use' }[s.model]}>
          <Segmented label="Pose model" value={s.model} onChange={(v) => set({ model: v })} options={[{ value: 'lite', label: 'Lite' }, { value: 'full', label: 'Full' }, { value: 'heavy', label: 'Heavy' }]} />
        </Row>
        <Row label="Processor" hint="Use CPU if the GPU path misbehaves on your device">
          <Segmented label="Processor" value={s.delegate} onChange={(v) => set({ delegate: v })} options={[{ value: 'GPU', label: 'GPU' }, { value: 'CPU', label: 'CPU' }]} />
        </Row>
        <Row label="Show skeleton">
          <Toggle label="Show skeleton" checked={s.showSkeleton} onChange={(v) => set({ showSkeleton: v })} />
        </Row>
      </div>

      <div className="section-title">Setups</div>
      <div className="card flush list">
        {app.calibrations.map((c) => (
          <div key={c.id} className="list-item">
            <div className="grow">
              <div style={{ fontWeight: 600 }}>
                {c.source === 'demo' ? 'Demo scene' : c.camera.label ?? 'Camera'} · {c.stance}
              </div>
              <div className="small faint">
                {c.tape ? 'Taped floor' : 'No tape'} · {fmtDate(c.createdAt)}
              </div>
            </div>
            {c.id === s.activeCalibrationId ? (
              <span className="badge clean">
                <Icon name="check" size={14} /> Active
              </span>
            ) : (
              <button className="btn small" onClick={() => set({ activeCalibrationId: c.id })}>
                Use
              </button>
            )}
            <button
              className="icon-btn plain"
              aria-label="Delete setup"
              onClick={async () => {
                if (!confirm('Delete this setup? Sessions recorded with it keep their results but can’t be re-scored.')) return;
                await db.deleteCalibration(c.id);
                if (c.id === s.activeCalibrationId) set({ activeCalibrationId: null });
                await app.reloadCalibrations();
              }}
            >
              <Icon name="trash" size={18} />
            </button>
          </div>
        ))}
        <button className="list-item" onClick={() => navigate('setup')}>
          <Icon name="camera" />
          <span className="grow">New setup</span>
          <Icon name="chevron" size={18} />
        </button>
      </div>

      <div className="section-title">Storage</div>
      <div className="card flush list">
        <Row label="Used on this device" hint={storage ? `${storage.persisted ? 'Protected from automatic clean-up' : 'May be cleared by the browser if space runs low'}` : undefined}>
          <span className="num muted">{storage ? mb(storage.usage) : '—'}</span>
        </Row>
        {storage && !storage.persisted && (
          <button className="list-item" onClick={async () => setStorage({ ...storage, persisted: await requestPersistence() })}>
            <Icon name="flag" />
            <span className="grow">Protect my data from clean-up</span>
          </button>
        )}
        <Row label="Keep video clips for" hint="Older sessions keep results and stills">
          <Segmented
            label="Keep clips"
            value={s.keepClipSessions}
            onChange={async (v) => {
              set({ keepClipSessions: v });
              const n = await pruneClips(v);
              if (n) app.toast(`Removed ${n} old clips`);
              void storageEstimate().then(setStorage);
            }}
            options={[{ value: 5, label: '5 sessions' }, { value: 15, label: '15' }, { value: 50, label: '50' }]}
          />
        </Row>
        <button
          className="list-item"
          style={{ color: 'var(--danger)' }}
          onClick={async () => {
            if (!confirm('Delete all setups, sessions, clips and settings from this device?')) return;
            releaseSource();
            await wipeEverything();
            location.hash = '';
            location.reload();
          }}
        >
          <Icon name="trash" />
          <span className="grow">Delete all data</span>
        </button>
      </div>

      <p className="tiny faint" style={{ padding: '12px 4px' }}>
        Everything — video, measurements and settings — stays on this device. Pose detection runs locally with Google’s MediaPipe Pose Landmarker. Measurements are
        approximate and compare you with your own recorded stance; they aren’t a judgement of technique.
      </p>
    </div>
  );
}
